import React from "react";
import { styled } from "@mui/material/styles";
import "./interactiveExparience.css";
import Button from "@mui/material/Button";
import Grow from "@mui/material/Grow";
import { CircularProgress } from "@mui/material";
import { Typography } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useDispatch, useSelector } from "react-redux";
import { randomNumber, useViewport } from "../../helpers";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import Logo from "../../assets/logoStudioPBADS.png";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ConfirmationDialog from "../dialog/ConfirmationDialog";
import Skeleton from "@mui/material/Skeleton";
import { NotificationsActive } from "@mui/icons-material";
import LockIcon from "@mui/icons-material/Lock";
import geolocIcon from "../../assets/geoloc.png";
import { useNavigate } from "react-router-dom/dist";
import { openBackdrop } from "../../reducers/expariences/expariencesSlice";
import {
  deleteExparience,
  fetchMyExpariences,
  updatePublication,
} from "../../reducers/expariences/expariencesThunks";
import { myExpariencesService } from "../../services";
import GoldenSwitch from "../goldenSwitch/GoldenSwitch";

const PREFIX = "interactiveExparience";

const classes = {
  root: `${PREFIX}-root`,
  dotsContainer: `${PREFIX}-dotsContainer`,
  dots: `${PREFIX}-dots`,
  backdrop: `${PREFIX}-backdrop`,
  zoneMenuActionsPopper: `${PREFIX}-zoneMenuActionsPopper`,
  MenuItemIcon: `${PREFIX}-MenuItemIcon`,
  designTitle: `${PREFIX}-designTitle`,
  leftMargin: `${PREFIX}-leftMargin`,
  paddingMenuList: `${PREFIX}-paddingMenuList`,
  designTitleAddCard: `${PREFIX}-designTitleAddCard`,
  securedExparienceIcon: `${PREFIX}-securedExparienceIcon`,
  securedByPositionIcon: `${PREFIX}-securedByPositionIcon`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled("div")(({ theme }) => ({
  [`& .${classes.dotsContainer}`]: {
    position: "absolute",
    bottom: 0,
    right: "-15px",
    width: "40px",
    height: "38px",
    cursor: "pointer",
    zIndex: 999,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  [`& .${classes.dots}`]: {
    "Webkit-transition": "all .4s ease",
    MozTransition: "all .4s ease",
    OTransition: "all .4s ease",
    transition: "all .4s ease",
    color: "#fff",
    "&:hover": {
      "Webkit-transition": "all .4s ease",
      MozTransition: "all .4s ease",
      OTransition: "all .4s ease",
      transition: "all .4s ease",
      color: theme.palette.primary.main,
    },
  },

  [`& .${classes.backdrop}`]: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },

  [`& .${classes.zoneMenuActionsPopper}`]: {
    zIndex: 999,
    "& > div": {
      backgroundColor: "#424242",
    },
  },

  [`& .${classes.MenuItemIcon}`]: {
    color: theme.palette.primary.main,
  },

  [`& .${classes.designTitle}`]: {
    maxWidth: "80%",
  },

  [`& .${classes.leftMargin}`]: {
    marginLeft: "12px !important",
  },

  [`& .${classes.paddingMenuList}`]: {
    padding: "10px",
  },

  [`& .${classes.designTitleAddCard}`]: {
    position: "relative",
    top: "1.5rem",
    width: "80%",
    margin: "0 auto",
    textAlign: "center",
  },

  [`& .${classes.securedExparienceIcon}`]: {
    position: "absolute",
    zIndex: 900,
    [theme.breakpoints.between("xs", "md")]: {
      right: "2%",
      top: "2%",
    },
    right: "10px",
    top: "10px",
    color: theme.palette.primary.main,
  },

  [`& .${classes.securedByPositionIcon}`]: {
    position: "absolute",
    width: "26px",
    height: "30px",
    zIndex: 998,
    [theme.breakpoints.between("xs", "md")]: {
      left: "2%",
      top: "2%",
    },
    left: "10px",
    top: "10px",
  },
}));

export default function InteractiveExparience(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useNavigate();
  const [isHovered, setHover] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [url, setUrl] = React.useState(props.myExparience.logoUrl);
  const [openDesktopMenu, setOpenDesktopMenu] = React.useState(false);
  const anchorRef = React.useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const { width } = useViewport();
  const [openConfirmation, setOpenConfirmation] = React.useState(false);
  const savingLoader = useSelector((state) => state.myExpariences.savingLoader);

  React.useEffect(() => {
    if (props.myExparience && props.myExparience.logoUrl) {
      setUrl(props.myExparience.logoUrl);
    } else {
      setUrl("undefined-url.jpg");
    }
  }, [props.myExparience]);

  const handleToggle = () => {
    setOpenDesktopMenu(!openDesktopMenu);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpenDesktopMenu(false);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpenDesktopMenu(false);
    }
  }

  function imageLoaded() {
    setLoading(false);
  }

  function setErrorUrl() {
    setUrl(Logo);
    setLoading(false);
  }

  const deleteMyExparience = async (id) => {
    try {
      const thunk = await dispatch(deleteExparience(id));
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      enqueueSnackbar(t("toasts.exparienceDeletedSuccess"), {
        variant: "success",
      });
      try {
        const thunk = await dispatch(
          fetchMyExpariences({ offset: 0, limit: 20, reset: true })
        );
        if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      } catch {
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      }
    } catch {
      enqueueSnackbar(t("toasts.exparienceDeletedError"), { variant: "error" });
    }
  };

  const updateMyPublication = async (id, published, isAdmin) => {
    try {
      const thunk = await dispatch(
        updatePublication({ id, value: published, isAdmin })
      );
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      enqueueSnackbar(t("toasts.updatePublicationExparienceSuccess"), {
        variant: "success",
      });
    } catch {
      enqueueSnackbar(t("toasts.updatePublicationExparienceError"), {
        variant: "error",
      });
    }
  };

  return (
    <Root>
      <ConfirmationDialog
        open={openConfirmation}
        setOpen={setOpenConfirmation}
        element={props.myExparience}
        onAgree={() => deleteMyExparience(props.myExparience._id)}
        mode="deleteExparience"
      />
      <div
        className="my-exparience-item"
        onMouseOver={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (props.addCard) {
            history("/myExpariences/create");
          } else if (props.redirectUrlWithId) {
            history(`${props.redirectUrlWithId}${props.myExparience._id}`);
          } else {
            history(`/myExpariences/${props.myExparience._id}`);
          }
        }}
      >
        {props.myExparience.securedByPassword && (
          <LockIcon className={classes.securedExparienceIcon} />
        )}
        {props.myExparience.securedByPosition && (
          <img
            src={geolocIcon}
            alt="secured by position"
            className={classes.securedByPositionIcon}
          />
        )}
        <Popper
          open={openDesktopMenu}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          className={classes.zoneMenuActionsPopper}
          placement="bottom-end"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="menu-list-grow" onKeyDown={handleListKeyDown}>
                    <MenuItem
                      className={classes.paddingMenuList}
                      onClick={
                        !savingLoader
                          ? (e) => {
                              e.stopPropagation();
                              updateMyPublication(
                                props.myExparience.id,
                                !props.myExparience.published,
                                props.isAdmin
                              );
                            }
                          : undefined
                      }
                    >
                      <GoldenSwitch
                        size="small"
                        disableMarginRight
                        checked={
                          props.myExparience.published ||
                          props.myExparience.published === null
                        }
                        name="publish/unpublish"
                      />
                      {savingLoader ? (
                        <CircularProgress
                          size={28}
                          color="primary"
                          style={{ marginLeft: "2rem" }}
                        />
                      ) : props.myExparience.published ||
                        props.myExparience.published === null ? (
                        <Typography
                          variant="body2"
                          className={classes.leftMargin}
                        >
                          {t("myExpariences.unpublishExparience")}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          className={classes.leftMargin}
                        >
                          {t("myExpariences.publishExparience")}
                        </Typography>
                      )}
                    </MenuItem>
                    <MenuItem
                      className={classes.paddingMenuList}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClose(e);
                        myExpariencesService
                          .sendNotification(
                            props.myExparience._id,
                            props.isAdmin
                          )
                          .then(() => {
                            enqueueSnackbar(t("toasts.notificationSent"), {
                              variant: "success",
                            });
                          })
                          .catch((err) => {
                            if (err === 403)
                              enqueueSnackbar(t("toasts.unauthorizedError"), {
                                variant: "error",
                              });
                            else
                              enqueueSnackbar(t("toasts.unknownError"), {
                                variant: "error",
                              });
                          });
                      }}
                    >
                      <NotificationsActive className={classes.MenuItemIcon} />
                      <Typography
                        variant="body2"
                        className={classes.leftMargin}
                      >
                        {t("myExpariences.notify")}
                      </Typography>
                    </MenuItem>
                    <MenuItem
                      className={classes.paddingMenuList}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenConfirmation(true);
                      }}
                    >
                      <DeleteForeverIcon className={classes.MenuItemIcon} />
                      <Typography
                        variant="body2"
                        className={classes.leftMargin}
                      >
                        {t("myExpariences.deleteExparience")}
                      </Typography>
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
        {props.addCard ? (
          <div className="my-exparience-add-item">
            <div className={classes.designTitleAddCard}>
              <AddIcon style={{ width: "50px", height: "50px" }} />
              <div>
                {props.myExparience.displayName
                  ? props.myExparience.displayName.toUpperCase()
                  : props.myExparience.name.toUpperCase()}
              </div>
            </div>
          </div>
        ) : (
          <React.Fragment>
            <img
              src={url}
              alt={
                props.myExparience.hasOwnProperty("displayName")
                  ? props.myExparience.displayName.toUpperCase()
                  : props.myExparience.hasOwnProperty("name")
                  ? props.myExparience.name.toUpperCase()
                  : "undefined"
              }
              className="my-exparience-image"
              style={{
                filter: isHovered && "blur(3px)",
                display: loading && "none",
              }}
              onError={setErrorUrl}
              onLoad={imageLoaded}
            />
            {!loading && (
              <div className="my-exparience-empty-item">
                <div className={classes.designTitle}>
                  <span>
                    {props.myExparience.displayName
                      ? props.myExparience.displayName.toUpperCase()
                      : props.myExparience.name.toUpperCase()}
                  </span>
                </div>
                {props.isAdmin && (
                  <Button
                    ref={anchorRef}
                    aria-controls={
                      openDesktopMenu ? "menu-list-grow" : undefined
                    }
                    aria-haspopup="true"
                    className={classes.dotsContainer}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (width > 425) {
                        handleToggle();
                      } else {
                        dispatch(
                          openBackdrop({
                            from: "exparience",
                            infos: { exparience: props.myExparience, url },
                          })
                        );
                      }
                    }}
                  >
                    <MoreHorizIcon className={classes.dots} />
                  </Button>
                )}
              </div>
            )}
            {loading && (
              <Skeleton
                variant="rectangular"
                style={{
                  height: randomNumber(50, 300),
                }}
              />
            )}
          </React.Fragment>
        )}
      </div>
    </Root>
  );
}
